<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent class="max-w-2xl">
            <DialogHeader>
                <template #icon>
                    <Globe class="text-z-primary-600" stroke-width="1.75"
                /></template>

                <DialogTitle>
                    {{
                        $t("domains.modal.title", { domain: domain.name })
                    }}</DialogTitle
                >
                <DialogDescription
                    class="max-w-[600px]"
                    v-html="$t('domains.modal.description')"
                >
                </DialogDescription>
            </DialogHeader>
            <div class="space-y-4 p-6">
                <div
                    class="rounded-xl border-iron-200 bg-iron-50 overflow-hidden border"
                >
                    <div
                        class="border-iron-200 bg-white px-4 py-3 flex items-center justify-between border-b"
                    >
                        <div class="gap-2 flex items-center">
                            <Terminal :size="16" class="text-iron-500" />
                            <span class="text-sm font-medium text-iron-700">
                                {{ $t("page_domains.dns_setup.dns_records") }}
                            </span>
                        </div>
                    </div>

                    <div class="divide-iron-200 divide-y">
                        <DnsRecordRow
                            v-for="(record, i) of records"
                            :key="i"
                            :type="record.type"
                            :type-color="typeColor(record.type)"
                            :name="record.name"
                            :value="record.value"
                            :field-key="`record_${i}`"
                            :copied-field="copiedField"
                            :validation="{
                                valid: record.status === 'verified',
                                current: null,
                            }"
                            :valid-label="$t('domains.record_verified')"
                            :invalid-label="$t('domains.record_pending')"
                            @copy="copyToClipboard"
                        />
                    </div>
                </div>

                <div>
                    <Button
                        as="a"
                        :href="
                            route('app.domains.downloads.dns', {
                                domain: domain.id,
                            })
                        "
                        variant="ghost"
                    >
                        {{ $t("domains.download") }}</Button
                    >
                </div>
            </div>
            <DialogFooter>
                <Button variant="outline" @click="opened = false">
                    {{ $t("close") }}</Button
                >
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import route from "ziggy-js";
import axios from "axios";
import { ref, onMounted } from "vue";

import { Globe, Terminal } from "lucide-vue-next";
import DnsRecordRow from "./DnsRecordRow.vue";

type DomainRecord = {
    type: "CNAME" | "TXT" | "MX";
    name: string;
    value: string;
    status: "verified" | "pending";
};

const opened = ref(false);
const records = ref<DomainRecord[]>([]);
const copiedField = ref<string | null>(null);

const props = defineProps<{
    domain: any;
}>();

const typeColor = (type: string): "blue" | "purple" | "green" | "amber" => {
    if (type === "CNAME") {
        return "blue";
    }
    if (type === "TXT") {
        return "purple";
    }
    if (type === "MX") {
        return "green";
    }
    return "amber";
};

const copyToClipboard = async (text: string, field: string) => {
    try {
        await navigator.clipboard.writeText(text);
        copiedField.value = field;
        setTimeout(() => {
            copiedField.value = null;
        }, 2000);
    } catch {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        copiedField.value = field;
        setTimeout(() => {
            copiedField.value = null;
        }, 2000);
    }
};

async function getDomainRecords() {
    const res = await axios.get(
        route("app.domains.show", { domain: props.domain.id }),
    );

    records.value = res.data.data;
}

onMounted(async () => {
    await getDomainRecords();
});
</script>
